<script setup lang="ts">
import { useAppStore } from '@/stores/app-store'
import { doLogout, getUrl } from '@/common/utility'
import { usePermission } from '@/composables/usePermission'
import type { Permission } from '@/composables/usePermission'
import {
  ADMIN_SETTINGS_DATA_LINKS,
  ADMIN_SETTINGS_INTEGRATION_LINKS,
  ADMIN_SETTINGS_MAINTENANCE_LINKS,
} from '@/constants/admin/settings/routes'

defineOptions({
  name: 'AppNavbar',
})

type NavbarLayout =
  | 'default'
  | 'academic-approval-list-own'
  | 'academic-approval-list-all'
  | 'academic-approval-workbench'

const props = withDefaults(
  defineProps<{
    active?: string
    layout?: NavbarLayout
  }>(),
  {
    layout: 'default' as NavbarLayout,
  },
)

const appStore = useAppStore()
const { can } = usePermission()

type MenuItem = [string, string, string, readonly Permission[] | null, Permission | null]

const settingsPermissions: readonly Permission[] = [
  ...ADMIN_SETTINGS_DATA_LINKS,
  ...ADMIN_SETTINGS_MAINTENANCE_LINKS,
  ...ADMIN_SETTINGS_INTEGRATION_LINKS,
].map((link) => link.permission)

const mainMenu: Array<MenuItem> = [
  ['home', '', 'Home', null, null],
  ['applications', `applications/`, 'Applications', ['VIEW_SUBMISSION'], null],
  ['rules', `rules/`, 'Rules', ['VIEW_RULE'], null],
  ['articulation', `articulation/`, 'Articulation', ['VIEW_ARTICULATION'], null],
  ['settings', `settings/`, 'Settings', settingsPermissions, null],
  ['users', `users/`, 'Users', ['VIEW_USER'], null],
  [
    'approval-list',
    `approval_list/`,
    'Academic Approvals',
    ['VIEW_OWN_ACADEMIC_APPROVALS'],
    'VIEW_ALL_ACADEMIC_APPROVALS',
  ],
  [
    'approval-list-all',
    `approval_list/all`,
    'Academic Approvals',
    ['VIEW_ALL_ACADEMIC_APPROVALS'],
    null,
  ],
]

function canAccessMenuItem(requiredPermissions: readonly Permission[] | null): boolean {
  return requiredPermissions === null || requiredPermissions.some((permission) => can(permission))
}
</script>

<template>
  <div :class="['logo-shell', `navbar-layout-${props.layout}`]">
    <div class="logo"></div>
  </div>
  <nav :class="['navbar navbar-expand', `navbar-layout-${props.layout}`]">
    <div class="container-fluid as-content-container">
      <ul class="navbar-nav me-auto">
        <template v-for="menuItem in mainMenu" :key="menuItem[0]">
          <li
            v-if="canAccessMenuItem(menuItem[3]) && (!menuItem[4] || !can(menuItem[4]))"
            class="nav-item"
          >
            <a
              class="nav-link"
              :class="
                menuItem[0] === props.active ||
                (props.active === 'approval-list' && menuItem[0] === 'approval-list-all')
                  ? 'active'
                  : ''
              "
              :href="getUrl(menuItem[1])"
            >
              {{ menuItem[2] }}
            </a>
          </li>
        </template>
      </ul>
      <a
        v-if="appStore.username"
        class="nav-link"
        :href="getUrl('logout')"
        @click.prevent="doLogout()"
      >
        Logout <span id="myUserName">{{ appStore.username }}</span>
      </a>
    </div>
  </nav>
</template>

<style scoped lang="scss">
.logo-shell {
  height: 102px;
  overflow: hidden;
  background: #fff;
}

.logo {
  height: 60px;
  margin-top: 1em;
  margin-bottom: 1em;
  margin-left: 3em;
  background-repeat: no-repeat;
  background-size: contain;
  background-image: url(/img/La_Trobe_University_logo.svg);
}

.navbar {
  background-color: #242424 !important;
  padding: 0;

  .nav-link {
    color: #9d9d9d;
    padding: 0.75rem 1rem;

    &:hover {
      color: #fff;
      text-decoration: none;
    }

    &.active {
      background: #000;
    }
  }

  &.navbar-layout-academic-approval-list-own,
  &.navbar-layout-academic-approval-list-all,
  &.navbar-layout-academic-approval-workbench {
    height: 3em;

    > .as-content-container {
      display: block;
      height: 3em;
      padding-right: 15px;
      padding-left: 15px;
    }

    .navbar-nav {
      display: inline-block;
    }

    .navbar-nav .nav-item,
    > .as-content-container > .nav-link {
      display: inline-block;
    }

    .nav-link {
      display: inline-block;
      height: 3em;
      padding: 0 1em;
      color: #9d9d9d;
      font-size: 1rem;
      line-height: 3em;
    }

    .nav-link:hover {
      color: #fff;
      text-decoration: none;
    }

    .nav-link.active {
      background: #000;
    }

    > .as-content-container > .nav-link {
      float: right;
    }
  }

  &.navbar-layout-academic-approval-list-own,
  &.navbar-layout-academic-approval-list-all {
    .nav-link {
      vertical-align: top;
    }
  }

  &.navbar-layout-academic-approval-list-all > .as-content-container {
    padding-right: 0;
    padding-left: 0;
  }

  &.navbar-layout-academic-approval-workbench {
    .navbar-nav .nav-item {
      position: relative;
    }

    .navbar-nav .nav-item + .nav-item {
      margin-left: 3.46875px;
    }

    .nav-link {
      vertical-align: baseline;
    }
  }

  @media (min-width: 768px) {
    &.navbar-layout-academic-approval-list-own > .as-content-container,
    &.navbar-layout-academic-approval-list-all > .as-content-container,
    &.navbar-layout-academic-approval-workbench > .as-content-container {
      max-width: 750px;
    }
  }

  @media (min-width: 992px) {
    &.navbar-layout-academic-approval-list-own > .as-content-container,
    &.navbar-layout-academic-approval-list-all > .as-content-container,
    &.navbar-layout-academic-approval-workbench > .as-content-container {
      max-width: 970px;
    }
  }

  @media (min-width: 1200px) {
    &.navbar-layout-academic-approval-list-own > .as-content-container,
    &.navbar-layout-academic-approval-list-all > .as-content-container,
    &.navbar-layout-academic-approval-workbench > .as-content-container {
      max-width: 1170px;
    }
  }

  @media (min-width: 1400px) {
    &.navbar-layout-academic-approval-list-all > .as-content-container {
      max-width: 1350px;
    }
  }

  @media (min-width: 1600px) {
    &.navbar-layout-academic-approval-list-own > .as-content-container,
    &.navbar-layout-academic-approval-list-all > .as-content-container,
    &.navbar-layout-academic-approval-workbench > .as-content-container {
      max-width: 1540px;
      padding-right: 0;
      padding-left: 0;
    }
  }
}

.logo-shell.navbar-layout-academic-approval-list-own,
.logo-shell.navbar-layout-academic-approval-list-all {
  .logo {
    margin-bottom: 2em;
  }
}
</style>
